import classnames from 'classnames'
import styles from './ProductPageUiLeftSide.module.scss'
import AnimatedTextLines from '@/components/AnimatedTextLines/AnimatedTextLines'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { DOC_TYPES, PRODUCTS_SLUG } from '@/data'

import SanityImage from '@/components/SanityImage/SanityImage'
import useStore from '@/store'
import gsap from 'gsap'
import { ZOOM_IMAGE_CLOSE_BUTTON_ID, ZOOM_IMAGE_DURATION, ZOOM_IMAGE_ID } from '@/components/ZoomImage/ZoomImage'
import { Flip } from 'gsap/dist/Flip'
import { PRODUCT_PAGE_GALLERY_CLOSE_BUTTON_ID } from '@/components/ProductPageGalleryView/ProductPageGalleryView'
import { focusElement } from '@/utils'
import LineButton from '@/components/LineButton/LineButton'

gsap.registerPlugin(Flip)

export const PRODUCT_PAGE_GALLERY_OPEN_BUTTON_ID = 'product-page-gallery-open-button'
export const DETAIL_IMAGE_ID = 'detailImageId-69'
export const DETAIL_IMAGE_BREAKPOINTS = {
  tablet: {
    width: 3000,
  },
  xs: {
    width: 1000,
  },
}

const ProductPageUiLeftSide = forwardRef(({ className, productData }, ref) => {
  const textLinesRefs = useRef({
    description: null,
    detailTitle: null,
    // downloadSpecSheetLabel: null,
    // finishesLabel: null,
    // closeButton: null,
    descriptionItalic: null,
  })
  const lineButtonRefs = useRef({
    finishes: null,
    downloadSpecSheet: null,
    closeButton: null,
  })
  const $styleLabelRef = useRef(null)
  const $descriptionTextRef = useRef(null)
  const productPageActiveImage = useStore(state => state.productPageActiveImage)
  const [styleLabel, setStyleLabel] = useState(null)
  const setZoomImageOpen = useStore(state => state.setZoomImageOpen)
  const zoomImageOpen = useStore(state => state.zoomImageOpen)
  const $detailImageContainerRef = useRef(null)
  const zoomImageHasBeenOpen = useRef(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const setProductGalleryImagesOpen = useStore(state => state.setProductGalleryImagesOpen)
  const [allowDetailClick, setAllowDetailClick] = useState(true)
  const [isAnimatedIn, setIsAnimatedIn] = useState(false)
  const [styleLabelOverlaps, setStyleLabelOverlaps] = useState(false)

  const animateZoomImage = (animatingIn = true) => {
    if (!$detailImageContainerRef.current) return

    gsap.killTweensOf($detailImageContainerRef.current)

    gsap.to($detailImageContainerRef.current, {
      '--y': animatingIn ? '0%' : '100%',
      duration: animatingIn ? 0.6 : 0.4,
      delay: animatingIn ? 0.25 : 0,
      ease: 'Power3.easeOut',
    })
  }

  const animateImageToPosition = () => {
    const largeImage = $detailImageContainerRef.current.querySelector(`.${styles.large}`)
    const $elToFitFrom = document.getElementById(ZOOM_IMAGE_ID)

    if (!$elToFitFrom || !$detailImageContainerRef.current) return

    setAllowDetailClick(false)

    const duration = ZOOM_IMAGE_DURATION * 0.3

    if (largeImage) {
      gsap.killTweensOf(largeImage)
      gsap.set(largeImage, {
        autoAlpha: 1,
      })
      gsap.to(largeImage, {
        autoAlpha: 0.0001,
        ease: 'Power3.easeOut',
        duration,
        onComplete: () => {
          setAllowDetailClick(true)
        },
      })
    }

    Flip.killFlipsOf([$detailImageContainerRef.current, $elToFitFrom])

    const state = Flip.getState($detailImageContainerRef.current)

    Flip.fit($detailImageContainerRef.current, $elToFitFrom, {
      scale: true,
    })

    Flip.to(state, {
      duration,
      ease: 'Power3.easeOut',
      scale: true,
    })
  }

  const checkForOverlap = () => {
    const descriptionItalicElement = $descriptionTextRef.current
    const styleLabelElement = $styleLabelRef.current

    if (!descriptionItalicElement || !styleLabelElement) {
      setStyleLabelOverlaps(false)
      return
    }

    const descriptionRect = descriptionItalicElement.getBoundingClientRect()
    const styleLabelRect = styleLabelElement.getBoundingClientRect()

    // Check if elements overlap
    const overlaps = !(
      descriptionRect.right < styleLabelRect.left ||
      descriptionRect.left > styleLabelRect.right ||
      descriptionRect.bottom < styleLabelRect.top ||
      descriptionRect.top > styleLabelRect.bottom
    )

    setStyleLabelOverlaps(overlaps)

    // Debug logging (can be removed in production)
    // if (overlaps !== styleLabelOverlaps) {
    //   console.log('Style label overlap changed:', overlaps)
    // }
  }

  useEffect(() => {
    if (zoomImageOpen) {
      zoomImageHasBeenOpen.current = true
    }

    if (zoomImageHasBeenOpen.current) {
      if (!zoomImageOpen) {
        animateImageToPosition()
      } else {
      }
    }
  }, [zoomImageOpen])

  useEffect(() => {
    if (productPageActiveImage) {
      let title = productData.title

      if (productPageActiveImage?.label) {
        title = productPageActiveImage?.label
      }

      setStyleLabel(title)
    }
  }, [productPageActiveImage, productData])

  // Monitor for overlap between descriptionItalic and styleLabel
  useEffect(() => {
    const handleResize = () => {
      checkForOverlap()
    }

    const handleScroll = () => {
      checkForOverlap()
    }

    // Initial check
    checkForOverlap()

    // Add event listeners
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleScroll)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isAnimatedIn, styleLabel, productPageActiveImage, productData])

  useImperativeHandle(ref, () => ({
    animateIn: () => {
      setIsAnimatedIn(true)
      Object.values(textLinesRefs.current).forEach(($ref, i) => {
        const key = Object.keys(textLinesRefs.current)[i]

        let config = {}

        if (key === 'description') {
          config = {
            stagger: 0,
          }
        }

        if ($ref) $ref.animate(config)
      })

      Object.values(lineButtonRefs.current).forEach($ref => {
        if ($ref) $ref.animateIn()
      })

      if ($styleLabelRef.current) {
        gsap.killTweensOf($styleLabelRef.current)
        gsap.to($styleLabelRef.current, {
          autoAlpha: 1,
          duration: 0.6,
        })
      }

      animateZoomImage(true)
    },
    animateOut: () => {
      setIsAnimatedIn(false)
      Object.values(textLinesRefs.current).forEach(($ref, i) => {
        const key = Object.keys(textLinesRefs.current)[i]

        let config = {}

        if (key === 'description') {
          config = {
            stagger: 0,
          }
        }

        if ($ref) $ref.animateOut(config)
      })

      Object.values(lineButtonRefs.current).forEach($ref => {
        if ($ref) $ref.animateOut()
      })

      if ($styleLabelRef.current) {
        gsap.killTweensOf($styleLabelRef.current)
        gsap.to($styleLabelRef.current, {
          autoAlpha: 0,
          duration: 0.35,
        })
      }

      animateZoomImage(false)
    },
    reset: () => {
      Object.values(textLinesRefs.current).forEach($ref => {
        if ($ref) $ref.reset()
      })

      Object.values(lineButtonRefs.current).forEach($ref => {
        if ($ref) $ref.reset()
      })

      setImageLoaded(false)
      animateZoomImage(false)
    },
  }))

  return (
    <div
      className={classnames(styles.ProductPageUiLeftSide, className, {
        [styles.isAnimatedIn]: isAnimatedIn,
        [styles.styleLabelOverlaps]: styleLabelOverlaps,
      })}
    >
      {/* Close button */}
      <LineButton
        link={{
          linkType: 'internal',
          link: {
            slug: PRODUCTS_SLUG,
            _type: DOC_TYPES.PAGE,
          },
        }}
        label="Close"
        showInitially={false}
        ref={ref => {
          lineButtonRefs.current.closeButton = ref
        }}
        className={styles.closeButton}
      ></LineButton>

      {/* Description */}
      <div className={styles.descriptionContainer}>
        <div className={styles.descriptionContainer__italic}>
          <AnimatedTextLines
            querySelector="span"
            ref={ref => {
              textLinesRefs.current.descriptionItalic = ref
            }}
            autoAnimate={false}
            className={classnames(styles.descriptionItalic__lines, styles.textLines)}
          >
            <span className={classnames(styles.descriptionItalic, styles.textLinesValue)}>i.</span>
          </AnimatedTextLines>
        </div>
        <AnimatedTextLines
          querySelector="span"
          ref={ref => {
            textLinesRefs.current.description = ref
          }}
          autoAnimate={false}
          className={classnames(styles.description__lines, styles.textLines)}
        >
          <span
            ref={$descriptionTextRef}
            className={classnames(styles.description, styles.textLinesValue)}
          >
            {productData?.productData?.description}
          </span>
        </AnimatedTextLines>
      </div>

      {/* Style Label */}
      <div className={styles.styleLabelContainer}>
        <span
          className={styles.style__label}
          ref={$styleLabelRef}
        >
          <span className={styles.style__label__number}>ii.</span>
          {styleLabel || ''}
        </span>
      </div>

      <div className={styles.bottom}>
        {productData?.productData?.zoomImage && (
          <div className={classnames(styles.detailContainer, { [styles.zoomImageOpen]: zoomImageOpen })}>
            <div className={styles.detailTitleContainer}>
              <AnimatedTextLines
                querySelector="span"
                ref={ref => {
                  textLinesRefs.current.detailTitle = ref
                }}
                autoAnimate={false}
                className={classnames(styles.detailTitle__lines, styles.textLines)}
              >
                <span className={classnames(styles.detailTitle, styles.textLinesValue)}>(Detail)</span>
              </AnimatedTextLines>
            </div>

            <button
              id={DETAIL_IMAGE_ID}
              className={styles.detailImageContainer}
              onClick={() => {
                focusElement(document.getElementById(ZOOM_IMAGE_CLOSE_BUTTON_ID))
                setZoomImageOpen(true)
              }}
              ref={$detailImageContainerRef}
              aria-label="Open zoom image"
              disabled={!allowDetailClick}
            >
              <SanityImage
                image={productData?.productData?.zoomImage}
                breakpoints={DETAIL_IMAGE_BREAKPOINTS}
                disableLoadedOpacity={true}
                className={classnames(styles.detailImage, styles.large)}
              />
              <SanityImage
                image={productData?.productData?.zoomImage}
                breakpoints={{
                  tablet: {
                    width: 400,
                  },
                  xs: {
                    width: 200,
                  },
                }}
                disableLoadedOpacity={true}
                className={classnames(styles.detailImage, styles.small, {
                  [styles.loaded]: imageLoaded,
                })}
                onLoad={() => {
                  setImageLoaded(true)
                }}
              />
            </button>
          </div>
        )}

        {productData?.productData?.specSheet?.url && (
          <div className={styles.downloadSpecSheetContainer}>
            <LineButton
              className={styles.downloadSpecSheetButton}
              ref={ref => {
                lineButtonRefs.current.downloadSpecSheet = ref
              }}
              link={{
                linkType: 'external',
                link: productData?.productData?.specSheet?.url,
              }}
              label="Download Spec Sheet"
              showInitially={false}
            />
            <DownIcon className={styles.downIcon} />
          </div>
        )}

        {/* Finishes Label */}
        {productData?.productData?.galleryImages?.length > 0 && (
          <LineButton
            id={PRODUCT_PAGE_GALLERY_OPEN_BUTTON_ID}
            onClick={() => {
              setProductGalleryImagesOpen(true)
              focusElement(document.getElementById(PRODUCT_PAGE_GALLERY_CLOSE_BUTTON_ID))
            }}
            className={styles.finishesLabelContainer}
            ariaLabel={`View ${productData?.title} finishes`}
            label="Finishes"
            showInitially={false}
            ref={ref => {
              lineButtonRefs.current.finishes = ref
            }}
          />
        )}
      </div>
    </div>
  )
})

export const DownIcon = ({ className }) => {
  return (
    <svg
      className={classnames(styles.downIcon, className)}
      viewBox="0 0 9 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 4.89304L4.9007 8.73757M4.9007 8.73757L8.8014 4.89304M4.9007 8.73757V0"
        stroke="currentColor"
        strokeWidth="0.468084"
      />
    </svg>
  )
}

ProductPageUiLeftSide.displayName = 'ProductPageUiLeftSide'

export default ProductPageUiLeftSide
